import React from "react";
import Image from "next/image";
import Link from "next/link";
import { GetStaticProps } from "next";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import { Button } from "@/components/ui/button";
import useAllPage from "src/hooks/dynamic/allPage";
// constants
import { EVariants } from "src/constants/variants";
// data
import { caseStudies } from "data/case-studies";

export default function CaseStudiesPage() {
  return (
    <>
      <CustomHead
        title="Case Studies | PetsApp"
        description="Discover how veterinary practices are transforming their operations and client communication with PetsApp."
      />
      <Layout headerProps={{ variant: EVariants.PRIMARY }}>
        <div className="min-h-screen">
          {/* Hero Section */}
          <section className="bg-primary py-16 md:py-24 relative overflow-hidden">
            {/* Top fade gradient */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#4285F4] to-primary pointer-events-none z-0"></div>
            <div className="container mx-auto px-4 md:px-6 relative z-10">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl text-white font-cooper mb-6">
                  How customers get the most out of PetsApp
                </h1>
                <div className="mt-8">
                  <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                    <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                      Book a demo
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Case Studies Grid */}
          <section className="py-16 md:py-24 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {caseStudies.map((study, index) => (
                  <div key={index} className="flex flex-col h-full">
                    <Link href={`/case-studies/${study.slug}`}>
                      <div className="group">
                        <div className="overflow-hidden rounded-lg mb-4">
                          <Image
                            src={study.image || "/placeholder.svg"}
                            alt={study.title}
                            width={500}
                            height={300}
                            className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h2 className="text-xl font-bold mb-2 text-white font-cooper group-hover:text-accent transition-colors">
                          {study.title}
                        </h2>
                      </div>
                    </Link>
                    <p className="text-white/80 mb-4 font-brown flex-grow">{study.description}</p>
                    <div className="mt-auto">
                      <Link href={`/case-studies/${study.slug}`}>
                        <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                          Read case study
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="bg-tertiary rounded-xl p-8 md:p-12 text-white text-center max-w-4xl mx-auto relative overflow-hidden">
                <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4">
                  <Image
                    src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fyorkie.png&w=3840&q=75"
                    alt="Yorkie illustration"
                    width={300}
                    height={300}
                    className="w-[300px] h-[300px]"
                  />
                </div>

                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-4">
                    Ready to transform your veterinary practice?
                  </h2>
                  <p className="text-white/90 mb-6 max-w-2xl mx-auto font-brown">
                    Join the hundreds of veterinary practices using PetsApp to transform their client communication and
                    business operations.
                  </p>
                  <div className="flex justify-center">
                    <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                      <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                        Schedule a demo
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const initialReduxState = await useAllPage(ctx.locale as string);

  return {
    props: {
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
    },
    revalidate: 600, // 10 Minutes Cache
  };
};

